import { Trans } from '@lingui/react'
import { type ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { NOW_PDP_BUYBOX_TIMER_CLICK } from 'shared/experiments/consts'
import {
  isRebrandingCOMFormalTextExperimentActive,
  isRebrandingCOMInformalTextExperimentActive,
  isRebrandingITActive,
} from 'shared/experiments/utils/rebranding'
import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { type IState, type NowDataState } from 'types/redux'
import { type Experiment } from 'types/shopConfig'
import IconCountdown from 'views/components/atoms/Icon/IconCountdown'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  getSameDaySlotsSortedBySecondsLeft,
  setTimeleft,
} from 'views/utils/nowCountdownTimer'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface INowCountdownContainer {
  publicRuntimeConfig: {
    pageProperties: {
      showCountDownPlaceholder: boolean
      experiments: Experiment[]
    }
  }
}

const NowCountdownContainer: (
  props: INowCountdownContainer
) => ReactElement = props => {
  const {
    publicRuntimeConfig: {
      pageProperties: { experiments = [], showCountDownPlaceholder },
    },
  } = props
  const nowData: NowDataState = useSelector((state: IState) => state.nowData)
  const [shouldDisplayNowCountDown, setShouldDisplayNowCountDown] =
    useState(false)
  const isRebrandingEnabled = isRebrandingITActive(experiments)
  const [shipToday, setShipToday] = useState(false)
  const dispatch = useDispatch()
  const slots = getSameDaySlotsSortedBySecondsLeft(nowData?.slots)
  const secondsLeft = slots.length ? slots[0]?.secondsLeft : 0
  const [countdownTime, setCountdownTime] = useState(secondsLeft.toString())

  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    setShouldDisplayNowCountDown(
      nowData?.nowEligible || nowData?.notEligibleReason === 40
    )
    if (nowData?.slots && nowData?.slots.length) {
      setShipToday(nowData.slots[0].deliveryDay !== 'morgen')
    }
  }, [nowData])

  useEffect(() => {
    setCountdownTime(secondsLeft?.toString())
  }, [secondsLeft])

  useEffect(() => {
    const interval = setTimeleft(countdownTime, setTimeLeft, setCountdownTime)

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [countdownTime])

  const openZipModal = () => {
    dispatch(
      updateNowDataSuccessAction({
        ...nowData,
        openZipLabelModal: true,
      })
    )
  }

  const isExperimentTextEnabled =
    isRebrandingCOMInformalTextExperimentActive(experiments) ||
    isRebrandingCOMFormalTextExperimentActive(experiments)

  const nowCountdownTranslationValues = {
    countdown: (
      <span className="u-font-weight--bold o-NowCountdown--countdown">
        {showCountDownPlaceholder ? '--:--:--' : timeLeft}
      </span>
    ),
  }

  return (
    <div data-clientside-hook="r-NowCountdownContainer">
      <div
        className="o-BuyBoxWithVariants__nowCountdown u-padding-x-small--sides u-padding--sides@medium"
        hidden={!shouldDisplayNowCountDown}
      >
        <div className="u-border--top o-BuyBoxWithVariants__nowCountdown__container">
          {isRebrandingEnabled && <IconCountdown />}
          <p className="u-color--gray-medium u-margin--top o-NowCountdown--wrapper">
            {shipToday ? (
              <span className="o-NowCountdown--text u-color--positive u-font-weight--bold">
                Lieferung HEUTE
              </span>
            ) : (
              <span className="o-NowCountdown--text u-color--positive u-font-weight--bold">
                Lieferung MORGEN
              </span>
            )}{' '}
            {isExperimentTextEnabled ? (
              <Trans
                id="nowCountdown.ifYouBuyInXMinutesRebranding"
                values={nowCountdownTranslationValues}
              />
            ) : (
              <Trans
                id="nowCountdown.ifYouBuyInXMinutes"
                values={nowCountdownTranslationValues}
              />
            )}{' '}
            <ButtonAsLink
              underlined
              name="openZipModal"
              inheritColor
              onClick={() => {
                openZipModal()
                trackOptimizelyEvent(NOW_PDP_BUYBOX_TIMER_CLICK)
              }}
            >
              <Trans id="general.label.seeDetails" />
            </ButtonAsLink>
          </p>
        </div>
      </div>
    </div>
  )
}

export default withShopAndRuntimeConfig(NowCountdownContainer)
